import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { useBoardMenuOptional } from '../../context/BoardMenuContext';
import {
  deleteBoard,
  getBoardCategoryConfigPath,
  getBoardList,
  isBoardCategoryEnabled,
  type BoardListItem,
} from '../../utils/board';
import { invalidateBoardMenu } from '../../utils/boardMenu';
import { CommonUtils } from '../../utils/commonUtils';
import { formatDateFromApi } from '../../utils/dateTime';
import { Edit, FolderTree, LayoutList, Plus, Search, Trash2 } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'all', label: '전체 상태' },
  { value: 'ACTIVE', label: '활성' },
  { value: 'INACTIVE', label: '비활성' },
  { value: 'PENDING', label: '대기중' },
];

const BoardList: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl } = useParams<{ projectCode: string }>();
  const boardMenu = useBoardMenuOptional();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('board-list');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [boards, setBoards] = useState<BoardListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedBoards, setSelectedBoards] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  const handleSelectAll = () => {
    CommonUtils.handleSelectAll(selectedBoards, boards, setSelectedBoards, (b) => b.code);
  };
  const handleSelectBoard = (code: string) => {
    CommonUtils.handleSelectItem(code, selectedBoards, setSelectedBoards);
  };

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();

    if (trimmedSearchTerm.length <= 1) {
      setDebouncedSearchTerm('');
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(trimmedSearchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!projectCodeFromUrl || !projectDetail) return;
    let cancelled = false;
    setError(null);
    setIsLoading(true);
    getBoardList(projectCodeFromUrl, {
      keyword: debouncedSearchTerm || undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      includePostCount: true,
    })
      .then((res) => {
        if (!cancelled) {
          setBoards(res.boards ?? []);
          setTotalCount(res.totalCount ?? res.boards?.length ?? 0);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '게시판 목록을 불러오지 못했습니다.');
          setBoards([]);
          setTotalCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, projectDetail, debouncedSearchTerm, selectedStatus]);

  const fetchBoards = async () => {
    if (!projectCodeFromUrl) return;
    setError(null);
    setIsLoading(true);
    try {
      const res = await getBoardList(projectCodeFromUrl, {
        keyword: debouncedSearchTerm || undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        includePostCount: true,
      });
      setBoards(res.boards ?? []);
      setTotalCount(res.totalCount ?? res.boards?.length ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : '게시판 목록을 불러오지 못했습니다.');
      setBoards([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBoard = async (board: BoardListItem) => {
    if (!projectCodeFromUrl) return;
    if (!window.confirm(`"${board.name}" 게시판을 삭제하시겠습니까?`)) return;

    try {
      await deleteBoard(projectCodeFromUrl, board.code);
      setSelectedBoards((prev) => prev.filter((code) => code !== board.code));
      if (boardMenu) {
        await boardMenu.invalidate();
      } else {
        await invalidateBoardMenu(projectCodeFromUrl);
      }
      await fetchBoards();
    } catch (err) {
      alert(err instanceof Error ? err.message : '게시판 삭제에 실패했습니다.');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return formatDateFromApi(dateString) || dateString;
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; text: string }> = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-800' },
      INACTIVE: { bg: 'bg-gray-100', text: 'text-gray-800' },
      PENDING: { bg: 'bg-amber-100', text: 'text-amber-800' },
    };
    const style = map[status] ?? { bg: 'bg-gray-100', text: 'text-gray-600' };
    const label = STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        {label}
      </span>
    );
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="게시판 설정"
          subtitle={
            projectCodeFromUrl
              ? `${projectDetail.name} (${projectCodeFromUrl})`
              : '게시판 목록을 관리합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="ml-2 text-red-800 underline"
                >
                  닫기
                </button>
              </div>
            )}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  <div className="relative flex-1 sm:max-w-xs min-w-[120px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="게시판명, 코드로 검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>

                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() =>
                      projectCodeFromUrl && navigate(`/projects/${projectCodeFromUrl}/boards/create`)
                    }
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    게시판 추가
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  총 <span className="font-medium text-gray-900">{totalCount}</span>개 게시판 /
                  <span className="ml-1">
                    검색결과 <span className="font-medium text-gray-900">{boards.length}</span>개
                  </span>
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1" />
                    활성: {boards.filter((b) => b.status === 'ACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-1" />
                    비활성: {boards.filter((b) => b.status === 'INACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-amber-400 rounded-full mr-1" />
                    대기중: {boards.filter((b) => b.status === 'PENDING').length}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="pl-4 pr-0 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={
                            selectedBoards.length === boards.length && boards.length > 0
                          }
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="pl-6 pr-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        코드
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        상태
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        게시판 명칭
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        게시물 수
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          카테고리
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        생성일
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">작업</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                          </div>
                        </td>
                      </tr>
                    ) : boards.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <LayoutList className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">등록된 게시판이 없습니다.</p>
                        </td>
                      </tr>
                    ) : (
                      boards.map((board) => {
                        const categoryEnabled = isBoardCategoryEnabled(board.settings);
                        return (
                        <tr key={board.code} className="hover:bg-gray-50">
                          <td className="pl-4 pr-0 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedBoards.includes(board.code)}
                              onChange={() => handleSelectBoard(board.code)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="pl-6 pr-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {board.code}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(board.status)}</td>
                          <td className="px-4 py-4 text-sm text-gray-900">{board.name}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700 text-right tabular-nums">
                            {Number(board.postCount ?? 0).toLocaleString()}
                          </td>
                          <td className="pl-6 pr-0 py-4 whitespace-nowrap">
                            {categoryEnabled && projectCodeFromUrl ? (
                              <Link
                                to={getBoardCategoryConfigPath(projectCodeFromUrl, board.code)}
                                className="inline-flex items-center px-3 py-1.5 rounded-lg transition-colors text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                                title="카테고리 설정"
                              >
                                <FolderTree className="w-4 h-4 mr-1.5" />
                                카테고리
                              </Link>
                            ) : (
                              <button
                                type="button"
                                disabled
                                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                                title="카테고리 미사용 게시판"
                              >
                                <FolderTree className="w-4 h-4 mr-1.5" />
                                카테고리
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(board.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                type="button"
                                className="text-gray-400 hover:text-blue-600"
                                title="수정"
                                onClick={() =>
                                  projectCodeFromUrl &&
                                  navigate(
                                    `/projects/${projectCodeFromUrl}/boards/${encodeURIComponent(board.code)}/update`
                                  )
                                }
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                className="text-gray-400 hover:text-red-600"
                                title="삭제"
                                onClick={() => handleDeleteBoard(board)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {selectedBoards.length > 0 && (
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{selectedBoards.length}개 게시판 선택됨</span>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        상태 변경
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        엑셀 다운로드
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        선택 삭제
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BoardList;
